import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  runTransaction,
} from "firebase/firestore";
import logger from "../../../../utils/logger";
import { db, fbAuth } from "../../../../services/firebaseService/connection";
import { getIdToken } from "firebase/auth";
import axios from "axios";
import useAdminDataStore, {
  selectCurrentGroupData,
} from "../../../../store/adminData/adminData";
import { toast } from "react-toastify";
import {
  GUIDE_ONBOARD,
  TOPIC_ASSIGN_LEADERS,
} from "../../../../constants/gettingStarted";
import usePackages from "../../../../hooks/usePackages";
import { assignGroupMemberNumberIfNeeded } from "../../../../services/firebaseService/endPoints/admin/members";
import { postUpdateCDNUtil } from "../../../../helperFunctions/util";
import useGlobalStore from "../../../../store/store";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { countries } from "countries-list";
import useFormLayout from "../../../../hooks/useFormLayout";
import { Controller, useForm } from "react-hook-form";
import {
  ChantCheckbox,
  ChantInput,
  ChantSelect,
} from "../../../../CommonComponents";
import { DatePicker } from "antd";
import { getDayjsFromTimestamp } from "../../../../utils/helper";

const useEditMember = (userDetails, userId, handleModalOpen) => {
  const { updateMember } = useGlobalStore.getState().members;

  const groupData = useAdminDataStore(selectCurrentGroupData);
  const { currentPackages, packagesBySeason, currentPackagesLoading } =
    usePackages();
  const { formLayout, formLayoutLoading } = useFormLayout();

  //Constants
  let countryNames = Object.values(countries).map((country) => {
    return { name: country.name };
  });

  let years = Array.from({ length: dayjs().year() - 1979 }, (_, index) => {
    return dayjs().year() - index;
  }).map((year) => ({ value: `${year}`, label: `${year}` }));

  const stripeSubscriptionUrl = useMemo(() => {
    return userDetails?.subscriptionDetail?.id
      ? `https://dashboard.stripe.com/connect/accounts/${groupData?.paymentDetails?.stripeAccountId}/subscriptions/${userDetails?.subscriptionDetail?.id}`
      : null;
  }, [
    groupData?.paymentDetails?.stripeAccountId,
    userDetails?.subscriptionDetail?.id,
  ]);

  //States
  const [isSaving, setIsSaving] = useState(false);
  const [sizes, setSizes] = useState([]);
  const [registrationLayout, setRegistrationLayout] = useState({});
  const [customFields, setCustomFields] = useState({});
  const [hasTshirt, setHasTshirt] = useState(false);
  const [hasChildren, setHasChildren] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const fieldsLoading = useMemo(() => {
    return currentPackagesLoading || formLayoutLoading;
  }, [currentPackagesLoading, formLayoutLoading]);

  const renewalDate = useMemo(() => {
    if (
      userDetails?.subscriptionDetail &&
      userDetails?.subscriptionDetail.lastDate
    ) {
      let lastDate = getDayjsFromTimestamp(
        userDetails?.subscriptionDetail.lastDate,
      );
      return lastDate.set("year", lastDate.get("year") + 1);
    }
    return 0;
  }, [userDetails]);

  //React Hook Form setup
  const { handleSubmit, setValue, watch, reset, control, formState } = useForm({
    defaultValues: {
      name: null,
      email: null,
      hometown: null,
      memberSince: null,
      phone: null,
      birthday: {},
      address1: null,
      address2: null,
      city: null,
      state: null,
      country: null,
      zip: null,
      officialClubMember: null,
      seasonTicketHolder: {},
      tshirtSize: null,
      referrer: null,
      chapter: null,
      title: null,
      leaderEmail: null,
      userRole: null,
      lockStatus: false,
      showLeadersProfile: false,
      notes: null,
      package: null,
      ...Object.keys(formLayout?.customFields || {})?.reduce((acc, curr) => {
        acc[curr] = null;
        return acc;
      }, {}),
    },
    mode: "onChange", // Enable real-time validation
  });

  // Watch form values for dynamic updates
  const watchedValues = watch();

  const resetState = () => {
    setSizes([]);
    setRegistrationLayout({});
    setCustomFields({});
    setHasTshirt(false);
    setHasChildren(false);
    setSelectedPackage(null);
    reset({});
  };

  const initializeData = () => {
    try {
      if (userDetails) {
        let packageValue = "";
        if (userDetails.package && userDetails.packageSeason) {
          packageValue = `${userDetails.package}|${userDetails.packageSeason}`;
        } else if (userDetails.package) {
          packageValue = userDetails.package;
        }

        let customData = {};
        const customFields = formLayout?.customFieldDetails || {};
        Object.keys(customFields).forEach((key) => {
          if (userDetails[key]) {
            customData[key] = userDetails[key];
          } else {
            customData[key] = null;
          }
        });

        reset({
          name: userDetails.name || "",
          email: userDetails.email || "",
          hometown: userDetails.hometown || "",
          memberSince: userDetails.memberSince || "",
          phone: userDetails.phone || "",
          birthday: userDetails.birthday || {},
          address1: userDetails.address1 || "",
          address2: userDetails.address2 || "",
          city: userDetails.city || "",
          state: userDetails.state || "",
          country: userDetails.country || "",
          zip: userDetails.zip || "",
          officialClubMember: userDetails.officialClubMember || "",
          seasonTicketHolder: userDetails.seasonTicketHolder || {},
          tshirtSize: userDetails.tshirtSize || "",
          referrer: userDetails.referrer || "",
          chapter: userDetails.chapter || "",
          title: userDetails.title || "",
          leaderEmail: userDetails.leaderEmail || "",
          userRole: userDetails.userRole || "",
          lockStatus: userDetails.lockStatus || false,
          showLeadersProfile: userDetails.showLeadersProfile || false,
          notes: userDetails.notes || "",
          package: packageValue,
          ...customData,
        });
      }

      if (userDetails?.package && userDetails?.packageSeason) {
        const currentPackage = currentPackages.find(
          (pack) =>
            String(pack?.id) === String(userDetails?.package) &&
            String(pack?.season) === String(userDetails?.packageSeason),
        );
        setSelectedPackage(currentPackage);
      }

      let currentPackage = userDetails.package
        ? currentPackages.find((pack) => pack.id === userDetails.package)
        : null;
      if (
        currentPackages.length > 0 &&
        userDetails.package &&
        currentPackage &&
        (currentPackage.includesTshirt || currentPackage.askTshirtSize)
      ) {
        setHasTshirt(true);
        const pkgSizes =
          currentPackages.find((pack) => pack.id === userDetails.package)
            .sizes ||
          currentPackages.find((pack) => pack.id === userDetails.package)
            .tshirtSizes;
        const sizesData = pkgSizes.map((size) => ({ name: size, value: size }));
        setSizes(sizesData);
      }

      if (
        currentPackages.length > 0 &&
        userDetails.package &&
        currentPackage &&
        (currentPackage.hasNoOfChildren ||
          currentPackage.includesChildPrice ||
          currentPackage.price.type === "Per Family")
      ) {
        setHasChildren(true);
      }

      // Package type logic removed as it's not being used

      setRegistrationLayout(formLayout || {});
      setCustomFields(formLayout?.customFieldDetails || {});
    } catch (error) {
      logger.error(error);
    }
  };

  const getCustomField = (fieldDetails, fieldKey) => {
    switch (fieldDetails.fieldType) {
      case "text":
        return (
          <ChantInput
            label={<span>{`${fieldDetails.fieldTitle}`}</span>}
            control={control}
            name={fieldKey}
            placeholder={fieldDetails.fieldHelp}
            tabIndex="2"
            disabled={isSaving}
          />
        );
      case "checkbox":
        return (
          <div className="col">
            <Controller
              name={fieldKey}
              control={control}
              render={() => (
                <ChantCheckbox
                  label={fieldDetails.fieldTitle}
                  control={control}
                  name={fieldKey}
                  disabled={isSaving}
                />
              )}
            />
          </div>
        );
      case "dropdown":
        return (
          <ChantSelect
            label={<span>{fieldDetails.fieldTitle}</span>}
            control={control}
            name={fieldKey}
            placeholder={fieldDetails.fieldHelp}
            tabIndex="2"
            disabled={isSaving}
            options={
              fieldDetails.dropdownValues?.map((value) => ({
                value,
                label: value,
              })) || []
            }
          />
        );
      case "number":
        return (
          <ChantInput
            label={<span>{`${fieldDetails.fieldTitle}`}</span>}
            control={control}
            name={fieldKey}
            type="number"
            placeholder={fieldDetails.fieldHelp}
            tabIndex="2"
            disabled={isSaving}
          />
        );
      case "date":
        return (
          <div>
            <label>{`${fieldDetails.fieldTitle}`}</label>
            <Controller
              name={fieldKey}
              control={control}
              render={({ field }) => (
                <DatePicker
                  format="MM/DD/YYYY"
                  tabIndex="2"
                  value={
                    field.value ? getDayjsFromTimestamp(field.value) : null
                  }
                  onChange={(date) => field.onChange(dayjs(date).valueOf())}
                  className="form-control form-control-sm col-8"
                  size={10}
                />
              )}
            />
          </div>
        );
      default:
        return <></>;
    }
  };

  const onSubmit = async (data) => {
    try {
      setIsSaving(true);
      if (!formState.isValid) {
        return;
      }

      // Only pass the fields that have actually changed
      const updatedData = {};
      Object.keys(formState.dirtyFields).forEach((key) => {
        updatedData[key] = data[key];
      });

      const response = await handleSave(updatedData, userId, userDetails.email);
      if (response?.success) {
        toast.success("Member details updated successfully");
        handleModalOpen();
        resetState();
      } else {
        toast.error(response?.message || "Failed to update member details");
      }
    } catch (error) {
      logger.error(error);
      toast.error("Failed to update member details");
      handleModalOpen();
      resetState();
    } finally {
      setIsSaving(false);
    }
  };

  const unsubscribeFromNotifications = async (groupId, userId) => {
    try {
      const token = await getIdToken(fbAuth.currentUser, false);

      await axios.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/notification/unSubscribeToTopics`,
        {
          groupIds: [groupId],
          userId: String(userId),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      logger.error("Failed to unsubscribe");
    }
  };

  const subscribeToNotifications = async (groupId, userId) => {
    try {
      const token = await getIdToken(fbAuth.currentUser, false);
      await axios.post(
        `${import.meta.env.VITE_GCP_BASE_URL}/notification/subscribeToTopics`,
        {
          groupIds: [groupId],
          userId: String(userId),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      logger.error("Failed to subscribe");
    }
  };

  const verifyDuplicateEmail = async (updatedEmail) => {
    try {
      // Check in invite_emails collection
      const docRef = doc(db, "invite_emails", groupData?.id);
      const docSnapshot = await getDoc(docRef);
      let emails = docSnapshot.exists() ? docSnapshot.data().emails || [] : [];
      const emailExistsInInvites = emails.some(
        (email) => email === updatedEmail,
      );

      // Check in userGroup members collection
      const userGroupQuery = query(
        collection(db, "userGroup", groupData?.id, "members"),
        where("email", "==", updatedEmail),
      );
      const userGroupQuerySnapshot = await getDocs(userGroupQuery);

      // Check in invite_emails/userDetails collection
      const inviteEmailsUserDetailsQuery = query(
        collection(db, "invite_emails", groupData?.id, "userDetails"),
        where("email", "==", updatedEmail),
      );
      const inviteEmailsUserDetailsSnapshot = await getDocs(
        inviteEmailsUserDetailsQuery,
      );

      if (
        emailExistsInInvites ||
        !userGroupQuerySnapshot.empty ||
        !inviteEmailsUserDetailsSnapshot.empty
      ) {
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  // Real Firestore transaction for email updates
  const updateEmailTransaction = async (
    groupId,
    memberId,
    oldEmail,
    updatedEmail,
  ) => {
    try {
      // Validate email input
      if (
        !updatedEmail ||
        typeof updatedEmail !== "string" ||
        updatedEmail.trim() === ""
      ) {
        return {
          success: false,
          message: "Invalid email address provided.",
        };
      }

      // Trim and normalize the email
      const normalizedEmail = updatedEmail.trim().toLowerCase();

      // First verify the new email doesn't exist anywhere
      const isEmailAvailable = await verifyDuplicateEmail(normalizedEmail);
      if (!isEmailAvailable) {
        return {
          success: false,
          message:
            "Email address in use. Emails must be unique as they are used during app registration.",
        };
      }

      // Pre-fetch docs that require queries (queries are not allowed inside transactions)

      // invite_emails/{groupId}/userDetails — for non-registered members
      const inviteEmailsQuery = query(
        collection(db, "invite_emails", groupId, "userDetails"),
        where("email", "==", oldEmail),
      );
      const inviteEmailsQuerySnapshot = await getDocs(inviteEmailsQuery);
      const inviteEmailsUserDetailsDoc = inviteEmailsQuerySnapshot.docs[0];

      // users collection by old email — fallback for registered members whose
      // auth UID may differ from their userGroup memberId
      const usersByEmailQuery = query(
        collection(db, "users"),
        where("email", "==", oldEmail),
      );
      const usersQuerySnapshot = await getDocs(usersByEmailQuery);
      const userDocByEmail = usersQuerySnapshot.docs[0];

      const inviteEmailsTopLevelRef = doc(db, "invite_emails", groupId);

      await runTransaction(db, async (transaction) => {
        const normalizedMemberId = String(memberId) || "";

        const memberDocRef = doc(
          db,
          "userGroup",
          groupId,
          "members",
          normalizedMemberId,
        );
        const userDocRef = doc(db, "users", normalizedMemberId);
        const inviteEmailsTopLevelSnap = await transaction.get(
          inviteEmailsTopLevelRef,
        );

        const memberDocSnapshot = await transaction.get(memberDocRef);
        const userDocSnapshot = await transaction.get(userDocRef);

        // Update userGroup member
        if (memberDocSnapshot.exists()) {
          transaction.update(memberDocRef, { email: normalizedEmail });
        }

        // Update users doc — try by memberId first, fall back to email query
        // (covers registered members whose auth UID differs from userGroup memberId)
        if (userDocSnapshot.exists()) {
          transaction.update(userDocRef, { email: normalizedEmail });
        } else if (userDocByEmail?.exists()) {
          transaction.update(userDocByEmail.ref, { email: normalizedEmail });
        }

        // Update invite_emails/{groupId}/userDetails
        if (inviteEmailsUserDetailsDoc?.exists()) {
          transaction.update(inviteEmailsUserDetailsDoc.ref, {
            email: normalizedEmail,
          });
        }

        // Update invite_emails/{groupId}.emails top-level array
        // (non-registered members are tracked here before they sign up)
        if (inviteEmailsTopLevelSnap.exists()) {
          const emails = inviteEmailsTopLevelSnap.data().emails || [];
          const idx = emails.indexOf(oldEmail);
          if (idx !== -1) {
            const updatedEmails = [...emails];
            updatedEmails[idx] = normalizedEmail;
            transaction.update(inviteEmailsTopLevelRef, {
              emails: updatedEmails,
            });
          }
        }
      });

      return {
        success: true,
      };
    } catch (error) {
      logger.error("Error while updating email", error);
      return {
        success: false,
        message: "Failed to update email. Please try again later.",
      };
    }
  };

  const handlePackageUpdate = (packageInput) => {
    const packageValue = packageInput?.split("|");
    const packageId = packageValue[0];
    const packageSeason = packageValue[1];

    const packageDetails = currentPackages.find(
      (pkg) =>
        String(pkg.id) === String(packageId) &&
        String(pkg.season) === String(packageSeason),
    );

    return {
      packageId: packageId,
      packageName: packageDetails?.name || "",
      packageSeason: packageSeason
        ? String(packageSeason)
        : String(packageDetails?.season || ""),
      membershipExpiryDate: packageDetails?.packageExpiryDate || "",
    };
  };

  const updateBirthdayInUsers = async (birthday, userId) => {
    try {
      const userDocRef = doc(db, "users", userId);
      const userDocSnapshot = await getDoc(userDocRef);

      if (!userDocSnapshot.exists()) {
        return;
      }
      const monthAbbrevToFull = {
        Jan: "January",
        Feb: "February",
        Mar: "March",
        Apr: "April",
        May: "May",
        Jun: "June",
        Jul: "July",
        Aug: "August",
        Sep: "September",
        Oct: "October",
        Nov: "November",
        Dec: "December",
      };
      const birthDay = {
        day: Number(birthday.date) || null,
        month: monthAbbrevToFull[birthday.month] ?? birthday.month,
        year: Number(birthday.year) || null,
      };

      await updateDoc(userDocRef, { birthDay });
    } catch (error) {
      logger.error("Error while updating birthday in users", error);
    }
  };

  const handleSave = async (updatedData, id, email) => {
    try {
      // Handle email updates using transaction if email is being changed
      if (updatedData?.email && updatedData?.email !== email) {
        const emailUpdateSuccess = await updateEmailTransaction(
          groupData?.id,
          id,
          email,
          updatedData.email,
        );

        if (!emailUpdateSuccess.success) {
          return {
            success: false,
            message: emailUpdateSuccess.message,
          };
        } else {
          if (Object.keys(updatedData)?.length === 1 && updatedData?.email) {
            updateMember(groupData?.id, id, updatedData);
            delete updatedData?.email;
            return {
              success: true,
            };
          }
        }
      }

      if (updatedData?.birthday) {
        updateBirthdayInUsers(updatedData.birthday, id);
      }

      if (updatedData?.userRole === "admin") {
        useAdminDataStore
          .getState()
          .setGettingStartedItemStatus(
            groupData?.id,
            GUIDE_ONBOARD,
            TOPIC_ASSIGN_LEADERS,
            true,
          );
      }

      if (
        updatedData?.userRole &&
        ["member", "fan"].includes(updatedData?.userRole)
      ) {
        updatedData.showLeadersProfile = false;
        updatedData.title = "";
      }

      if (Object.prototype.hasOwnProperty.call(updatedData, "package")) {
        const { membershipExpiryDate, packageSeason, packageName, packageId } =
          handlePackageUpdate(updatedData?.package);

        updatedData = {
          ...updatedData,
          package: packageId,
          packageSeason,
          packageName,
          membershipExpiryDate,
        };
      }

      // if inv emails -> update inv emails
      const inviteEmailsDocSnap = await getDocs(
        query(
          collection(db, "invite_emails", groupData?.id, "userDetails"),
          where("email", "==", email),
        ),
      ).then((snap) => {
        return snap.docs[0];
      });

      if (inviteEmailsDocSnap && inviteEmailsDocSnap.exists()) {
        // Only update the fields that have changed
        await updateDoc(inviteEmailsDocSnap.ref, updatedData).catch((err) => {
          logger.error(
            "Error while updating userDetails in invite_emails",
            err,
          );
        });
      }

      // Invite-only member (not registered yet) given a package by admin:
      // number them on their invite_emails doc.
      if (
        typeof id !== "string" &&
        inviteEmailsDocSnap?.exists() &&
        Object.prototype.hasOwnProperty.call(updatedData, "package")
      ) {
        await assignGroupMemberNumberIfNeeded(groupData?.id, null, email);
      }

      if (typeof id === "string") {
        if (!updatedData.tshirtSize) {
          updatedData.tshirtSize = deleteField();
        }

        // Check if member exists in userGroup before updating
        const memberDocRef = doc(db, "userGroup", groupData?.id, "members", id);
        const memberDocSnapshot = await getDoc(memberDocRef);

        if (memberDocSnapshot.exists()) {
          // update only changed fields in usergroup collection
          await updateDoc(memberDocRef, updatedData);

          if (Object.prototype.hasOwnProperty.call(updatedData, "package")) {
            await assignGroupMemberNumberIfNeeded(groupData?.id, id, email);
          }
        }

        if (updatedData?.userRole && updatedData?.userRole?.length > 0) {
          postUpdateCDNUtil(groupData?.id);
        }

        if (updatedData?.lockStatus === true) {
          await unsubscribeFromNotifications(groupData?.id, id);
        }

        if (updatedData?.lockStatus === false) {
          await subscribeToNotifications(groupData?.id, id);
        }

        // Update website collection only if title changed
        if (updatedData.title) {
          const websiteCollectionRef = doc(db, "website", groupData?.id);
          const websiteDoc = await getDoc(websiteCollectionRef);

          if (websiteDoc.exists() && websiteDoc.data().about) {
            const about = websiteDoc.data().about;
            const leadership = about.leadership;

            if (leadership && Array.isArray(leadership.leaders)) {
              const requiredDoc = leadership.leaders.find(
                (value) => value.id === id,
              );
              if (requiredDoc) {
                requiredDoc.title = updatedData.title;
                await updateDoc(websiteCollectionRef, { about });
              }
            }
          }
        }

        // Handle hometown field separately (it's stored as homeTown in users collection)
        if (Object.prototype.hasOwnProperty.call(updatedData, "hometown")) {
          const homeTown = updatedData.hometown || "";
          await updateDoc(doc(db, "users", id), { homeTown });
        }
      }

      updateMember(groupData?.id, id, updatedData);

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        message: "Failed to update member details",
      };
    }
  };

  useEffect(() => {
    if (groupData?.id && userDetails?.id) {
      initializeData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id, userDetails?.id]);

  return {
    resetState,
    onSubmit,
    watchedValues,
    setValue,
    control,
    formState,
    isSaving,
    fieldsLoading,
    renewalDate,
    sizes,
    registrationLayout,
    customFields,
    countryNames,
    years,
    packagesBySeason,
    currentPackages,
    setSelectedPackage,
    hasTshirt,
    hasChildren,
    selectedPackage,
    getCustomField,
    handleSubmit,
    stripeSubscriptionUrl,
    groupData,
  };
};

export default useEditMember;
